import { defineComponent as _defineComponent } from 'vue'
import { createVNode as _createVNode, createElementVNode as _createElementVNode, unref as _unref, withCtx as _withCtx, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode } from "vue"

const _hoisted_1 = { class: "overflow-y-auto no-scrollbar h-full w-full" }
const _hoisted_2 = { class: "max-w-6xl mx-auto pt-2 xl:pt-[81px] md:pb-4 px-1 flex flex-col md:flex-row gap-4" }
const _hoisted_3 = { class: "flex flex-col w-full md:w-[53%] gap-4 inline-size" }
const _hoisted_4 = { class: "flex flex-col w-full md:w-[47%] gap-4" }
const _hoisted_5 = { class: "max-w-6xl mx-auto px-1 mt-4" }
const _hoisted_6 = {
  key: 0,
  class: "max-w-6xl mx-auto px-1 my-4"
}

import BirthDateInputCard from "@/views/Calculators/DestinyMatrix/components/BirthDateInputCard.vue";
import { computed, onBeforeMount, ref } from "vue";
import { DestinyMatrix } from "@/types/destinyMatrix";
import { downloadPdfResults, fetchDestinyMatrix } from "@/api/destinyMatrix";
import DestinyMatrixComponent from "@/views/Calculators/DestinyMatrix/components/DestinyMatrixComponent.vue";
import HealthMapComponent from "@/views/Calculators/DestinyMatrix/components/HealthMapComponent.vue";
import PersonalCardComponent from "@/views/Calculators/DestinyMatrix/components/PersonalCardComponent.vue";
import MatrixDescriptionComponent from "@/views/Calculators/DestinyMatrix/components/MatrixDescriptionComponent.vue";
import { useUserStore } from "@/stores/userStore";
import { PREMIUM_CALCULATORS } from "@/types/premiumTypes";
import { datePartsToString, normalizeStringDate } from "@/utils/date";
import { useRoute } from "vue-router";
import GradientButton from "@/components/buttons/GradientButton.vue";
import { useI18n } from "vue-i18n";


export default /*@__PURE__*/_defineComponent({
  __name: 'DestinyMatrixView',
  setup(__props) {

const authStore = useUserStore();
const route = useRoute();
const { t } = useI18n();

const waitApiCall = ref(false);
const destinyMatrix = ref<DestinyMatrix>();
const initDate = ref<Date>();
const userName = ref<string | null>(null);
const submittedDate = ref("");
const userHasSubscription = ref(false);
const userHasAssessToResults = computed(() => {
  if (userHasSubscription.value) return true;

  return isUserPurchasedDate(submittedDate.value);
});

onBeforeMount(async () => {
  await initTodayMatrix();
});

async function getMatrixData(day: number, month: number, year: number) {
  const newRequestedDate = datePartsToString(day, month, year);
  try {
    waitApiCall.value = true;
    destinyMatrix.value = await fetchDestinyMatrix(day, month, year);
    submittedDate.value = newRequestedDate;
  } catch (error) {
    console.log(error);
  } finally {
    waitApiCall.value = false;
  }
}

async function initTodayMatrix() {
  initDate.value = getInitDate();
  await authStore.updateUserInfo();
  userHasSubscription.value = authStore.userHasSubscription(
    PREMIUM_CALCULATORS.destinyMatrix
  );

  const requestedDate = datePartsToString(
    initDate.value.getDate(),
    initDate.value.getMonth() + 1,
    initDate.value.getFullYear()
  );

  destinyMatrix.value = await fetchDestinyMatrix(
    initDate.value.getDate(),
    initDate.value.getMonth() + 1,
    initDate.value.getFullYear()
  );
  submittedDate.value = requestedDate;
}

function getInitDate() {
  const dateString = route.query.init_date;
  if (!dateString) {
    return new Date();
  }
  const initDate = normalizeStringDate(dateString as string);

  return initDate ?? new Date();
}

function isUserPurchasedDate(requestedDate: string): boolean {
  if (authStore.user === null) return false;

  const products = authStore.user.products;

  return products["destiny_matrix"]?.includes(requestedDate) ?? false;
}

async function downloadPDF() {
  if (submittedDate.value === "") {
    return;
  }
  try {
    waitApiCall.value = true;
    const apiDate = submittedDate.value.split("-").reverse().join("-");
    const blob = await downloadPdfResults(apiDate, userName.value);
    const downloadUrl = window.URL.createObjectURL(
      new Blob([blob], { type: "application/pdf" })
    );
    const link = document.createElement("a");
    link.href = downloadUrl;
    link.setAttribute("download", `Matrix_${apiDate}.pdf`);
    document.body.appendChild(link);
    link.click();
    if (link.parentNode) {
      link.parentNode.removeChild(link);
    }
    window.URL.revokeObjectURL(downloadUrl);
  } catch (error) {
    console.log(error);
  } finally {
    waitApiCall.value = false;
  }
}

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createElementVNode("div", _hoisted_2, [
      _createElementVNode("div", _hoisted_3, [
        _createVNode(BirthDateInputCard, {
          class: "date-input",
          isLoading: waitApiCall.value,
          initDate: initDate.value,
          onCalculateMatrix: getMatrixData,
          modelValue: userName.value,
          "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((userName).value = $event))
        }, null, 8, ["isLoading", "initDate", "modelValue"]),
        _createVNode(DestinyMatrixComponent, {
          class: "schema aspect-square shrink-0",
          matrix: destinyMatrix.value
        }, null, 8, ["matrix"])
      ]),
      _createElementVNode("div", _hoisted_4, [
        _createVNode(HealthMapComponent, {
          class: "health-map",
          matrix: destinyMatrix.value
        }, null, 8, ["matrix"]),
        _createVNode(PersonalCardComponent, {
          class: "personal-card",
          matrix: destinyMatrix.value
        }, null, 8, ["matrix"])
      ])
    ]),
    _createElementVNode("div", _hoisted_5, [
      _createVNode(MatrixDescriptionComponent, {
        class: "accordion",
        matrix: destinyMatrix.value,
        submittedDate: submittedDate.value,
        userHasPremiumAccess: userHasAssessToResults.value
      }, null, 8, ["matrix", "submittedDate", "userHasPremiumAccess"])
    ]),
    (userHasSubscription.value || userHasAssessToResults.value)
      ? (_openBlock(), _createElementBlock("div", _hoisted_6, [
          _createVNode(GradientButton, {
            class: "rounded-xl shrink-0 h-10",
            "bg-color": "bg-brown-gradient",
            "hover-bg-color": "bg-orange-gradient",
            text: _unref(t)('buttons.download-as-pdf'),
            onClick: downloadPDF
          }, {
            icon: _withCtx(() => [...(_cache[1] || (_cache[1] = [
              _createElementVNode("div", null, null, -1)
            ]))]),
            _: 1
          }, 8, ["text"])
        ]))
      : _createCommentVNode("", true)
  ]))
}
}

})